// Emissão de cobrança avulsa — a sequência inteira, num lugar só.
//
// Extraído de `views/Financeiro/BoletoAvulsoModal.tsx` quando a cobrança
// unificada (Central de Inadimplência) passou a precisar da mesma coisa. São
// cinco passos que **têm** que acontecer nesta ordem, e cada um deles existe por
// causa de um incidente:
//
//   1. cria a fatura AVULSO (isenta da trava de duplicata, ver
//      `prevent_duplicate_active_fatura`);
//   2. resolve em qual conta Sicoob o boleto sai — a Vigor tem três, e a conta
//      é da UC (`conta_sicoob`), não da fatura. Errar aqui faz o cancelamento
//      pedir baixa na conta onde o título não existe (erro 5002);
//   3. chama o webhook do n8n que registra no banco;
//   4. **confere que a cobrança existe de verdade** antes de avançar o status —
//      200 do webhook não prova nada: em 31/07/2026 o Sicoob devolveu 500 por
//      ~2h, o n8n seguia e gravava linha digitável vazia;
//   5. só então carimba 'Com boleto emitido / A enviar'.
//
// Duplicar isso numa segunda tela era garantia de divergir no primeiro ajuste.

import { supabase } from './supabase';
import { emissaoBoletoValida } from './emissaoBoletoValida';
import { exigirContaLiberada, resolveContaEmissao } from './sicoobAccount';
import { camposEmissaoSicoob, type ContaSicoob } from './contaSicoob';
import { criarCobrancaAsaas, emitirBoletoTeste } from './gatewayResolver';

export interface ParametrosEmissaoAvulsa {
  /** Campos do INSERT em `faturas`. Quem chama monta os valores do boleto; o
   *  helper acrescenta status e classificação. */
  campos: Record<string, any>;
  /** UC que decide a conta Sicoob. */
  ucCodigo: string;
  /** Nome de quem emitiu, para o carimbo. */
  nomeQuem: string;
  /**
   * Roda logo depois de a fatura existir e ANTES de o boleto ser registrado no
   * banco. É onde a cobrança unificada grava o vínculo fatura-avulsa ↔ faturas
   * cobertas.
   *
   * A ordem importa: gravar o vínculo depois da emissão abriria uma janela em
   * que o boleto existe e nada o liga às faturas — se a gravação falhasse ali,
   * o cliente pagaria e nenhuma fatura seria quitada. Falhando aqui, o que sobra
   * é uma fatura em 'Aguardando processamento' sem boleto nenhum: ninguém paga,
   * ninguém é cobrado errado.
   */
  aposCriarFatura?: (faturaId: number) => Promise<void>;
  /**
   * Emissão de TESTE (só adm). Não fala com banco nenhum: o sandbox do Sicoob é
   * um catálogo de mocks e não registra boleto — quem preenche a fatura com
   * dados fictícios não pagáveis e a marca `ambiente='sandbox'` é a edge
   * `emitir-boleto-teste`. A fatura sai de todos os relatórios.
   */
  sandbox?: boolean;
  /**
   * Conta Sicoob decidida por quem chama, em vez da dedução pela UC. É o caso do boleto agrupado
   * (#5785): a conta é do agrupamento e o `ucCodigo` nem é uma UC.
   */
  conta?: ContaSicoob;
  /**
   * Por onde a cobrança sai. `'sicoob'` (padrão) é a conta da Vigor, via n8n.
   * `'asaas_parceira'` é a conta Asaas da empresa parceira da fatura, pela edge
   * `asaas-cobranca` — hoje só o boleto avulso de cliente próprio da Gran. Aqui
   * `sandbox` é emissão de verdade na conta de testes do Asaas, e quem chama grava
   * `ambiente: 'sandbox'` já no INSERT.
   */
  emissor?: 'sicoob' | 'asaas_parceira';
}

export interface ResultadoEmissaoAvulsa {
  faturaId: number;
  /** A linha como ficou depois da emissão (já com nosso número e linha digitável). */
  fatura: any;
}

/** Status inicial do boleto pela data de vencimento — nasce 'Vencido' quando a
 *  data já passou, que é comum em negociação de dívida antiga. */
export function statusInicialPorVencimento(vencimentoIso: string | null | undefined): string {
  if (!vencimentoIso) return 'Em aberto';
  const [ano, mes, dia] = String(vencimentoIso).split('T')[0].split('-').map(Number);
  const vencimento = new Date(ano, (mes || 1) - 1, dia || 1);
  vencimento.setHours(0, 0, 0, 0);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return vencimento < hoje ? 'Vencido' : 'Em aberto';
}

export async function emitirCobrancaAvulsa(
  { campos, ucCodigo, nomeQuem, aposCriarFatura, sandbox, conta: contaDecidida, emissor = 'sicoob' }: ParametrosEmissaoAvulsa,
): Promise<ResultadoEmissaoAvulsa> {
  // A conta é conferida antes de a fatura existir: conta travada depois do INSERT
  // deixaria para trás uma fatura sem boleto.
  const vaiAoSicoob = emissor === 'sicoob' && !sandbox;
  const conta = vaiAoSicoob ? (contaDecidida ?? await resolveContaEmissao(ucCodigo)) : contaDecidida;
  if (vaiAoSicoob && conta) await exigirContaLiberada(conta);

  // 1. Fatura
  const { data: novaFatura, error: erroFatura } = await supabase
    .from('faturas')
    .insert({
      ...campos,
      status_fatura: 'Aguardando processamento',
      status_boleto_vigor: statusInicialPorVencimento(campos.vencimento_boleto),
      classificacao: 'AVULSO',
    })
    .select('*')
    .single();

  if (erroFatura) throw new Error('Erro ao criar registro de fatura: ' + erroFatura.message);

  if (aposCriarFatura) await aposCriarFatura(novaFatura.id);

  const faturaEmitida = await registrarBoletoDaFatura(novaFatura, { ucCodigo, nomeQuem, sandbox, emissor, conta });
  return { faturaId: novaFatura.id, fatura: { ...novaFatura, ...faturaEmitida } };
}

/**
 * Passos 2 a 5 para uma fatura que JÁ existe: registra o boleto no banco, confere
 * que a cobrança existe de verdade e só então carimba 'Com boleto emitido / A
 * enviar'. É o que a entrada do acordo de parcelamento usa — a fatura dela nasce
 * na criação do acordo e o boleto sai depois, junto com o termo.
 *
 * Quem chama confere antes se a fatura já tem boleto: isto aqui não olha, e
 * chamar duas vezes cria duas cobranças.
 */
export async function registrarBoletoDaFatura(
  novaFatura: any,
  { ucCodigo, nomeQuem, sandbox, emissor = 'sicoob', conta: contaDecidida }: Pick<ParametrosEmissaoAvulsa, 'ucCodigo' | 'nomeQuem' | 'sandbox' | 'emissor' | 'conta'>,
): Promise<any> {
  // 2 e 3. Emissão.
  //
  // Em teste o caminho é outro de ponta a ponta: nada de webhook, nada de conta
  // Sicoob. A edge preenche a fatura com boleto fictício e a carimba como
  // sandbox — é assim que o resto do sistema já testa emissão.
  if (emissor === 'asaas_parceira') {
    // Conta Asaas da parceira: a edge cria a cobrança e grava linha digitável e PIX
    // na fatura. Nada de n8n nem de conta Sicoob.
    await criarCobrancaAsaas(novaFatura.id, 'BOLETO', sandbox ? 'sandbox' : 'producao');
  } else if (sandbox) {
    await emitirBoletoTeste(novaFatura.id);
  } else {
    const { data: settings } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', 'boleto_emission_webhook_url')
      .single();

    const webhookUrl = settings?.value;
    if (!webhookUrl) {
      throw new Error('URL do Webhook de Emissão de Boleto não configurada.');
    }

    const conta = contaDecidida ?? await resolveContaEmissao(ucCodigo);
    await exigirContaLiberada(conta);

    const resposta = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...novaFatura, ...camposEmissaoSicoob(conta) }),
    });

    if (!resposta.ok) {
      let mensagem = `Falha na comunicação com o emissor (Status: ${resposta.status})`;
      try {
        const corpo = await resposta.json();
        mensagem = corpo?.mensagem || corpo?.message || corpo?.error?.message || mensagem;
      } catch (e) { /* corpo não-JSON: fica a mensagem genérica */ }
      throw new Error(mensagem);
    }
  }

  // 4. A cobrança existe mesmo?
  const { data: faturaEmitida } = await supabase
    .from('faturas')
    .select('num_cobranca_boleto, linha_digitavel_boleto, url_boleto, empresa_parceira_id')
    .eq('id', novaFatura.id)
    .single();

  // O sandbox do Asaas cria cobrança de verdade (na conta de testes), então ali a
  // prova vale igual; só o teste do Sicoob, que não fala com banco, passa direto.
  const emissao = emissaoBoletoValida(faturaEmitida || {}, { sandbox: sandbox && emissor === 'sicoob' });
  if (!emissao.ok) throw new Error(emissao.motivo);

  // 5. Avança o status
  const { error: erroStatus } = await supabase
    .from('faturas')
    .update({
      status_fatura: 'Com boleto emitido / A enviar',
      emitido_por_nome: sandbox ? `${nomeQuem} (teste)` : nomeQuem,
      emitido_em: new Date().toISOString(),
    })
    .eq('id', novaFatura.id);

  // supabase-js devolve { error } em vez de lançar. Ignorar isso é o que fez a
  // trava de integridade virar cobrança duplicada em 24/08/2026: o operador via
  // sucesso, o card não saía do lugar e ele emitia de novo.
  if (erroStatus) {
    throw new Error(
      `A cobrança FOI criada${faturaEmitida?.num_cobranca_boleto ? ` (${faturaEmitida.num_cobranca_boleto})` : ''}, `
      + `mas a fatura não pôde avançar de status: ${erroStatus.message}. `
      + 'NÃO emita de novo — isso criaria uma segunda cobrança para o mesmo cliente. '
      + 'Abra a fatura pela tela de Faturas e cancele o boleto antes de qualquer nova tentativa.',
    );
  }

  return faturaEmitida || {};
}
